<template>
    <div class='online-deposit'>
        <div class="section-title">{{ $t(title) | capitalize }}</div>
        <GenericForm
            :fields="fields"
            :asyncErrors="fieldErrors"
            @submit="onSubmit"
        >
            <template slot-scope="slotProps">
                <div class="deposit-form">
                    <TypeSwitcher
                        :types="amounts"
                        :selected="fields.amount.value"
                        name="amount"
                        @input="onAmountClick"
                        @valueChanged="slotProps.onValueChanged"
                        class="deposit-amount"
                    />
                    <NumberInput
                        class="deposit__input"
                        v-model="fields.amount.value"
                        name="amount"
                        :label="''"
                        :unit="$t('currencyAbbreviation')"
                        :errorMessage="slotProps.errors.amount"
                        @valueChanged="slotProps.onValueChanged"
                        :placeholder="$t('') | capitalize"
                        ref="depositValue"
                    />
                    <button
                        type="submit"
                        class="btn btn__primary btn--lg btn--w-96 btn__primary--yellow"
                    >
                        {{ $t('deposit') | upperCase }}
                    </button>
                </div>
                <TaxLimitsSummary
                    v-if="isTaxable"
                    :amount="fields.amount.value"
                    :taxLimits="config.deposit.tax"
                    label="deposit"
                />
            </template>
        </GenericForm>
        <slot />
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex';
    import config from '@config';
    import Modals from '@store/modules/ui/shared/modal/modals';
    import UserService from '@core/services/user/UserService';
    import { min, max, isRequired } from '@modules/shared/validators/forms';
    import ValidationError from '@core/errors/network/ValidationError';
    import { errorGuard } from '@app/utils';
    import eventChannel from '@core/tracking/EventChannel';
    import Topic from '@core/tracking/Topic';

    export default {
        name: 'OnlineDepositContainer',
        props: {
            showWelcomeBonus: {
                type: Boolean,
                required: false,
                default: false,
            },
            showHelp: {
                type: Boolean,
                required: false,
                default: true,
            },
            title: {
                type: String,
                default: 'Deposit Online',
            },
            paySafeCard: {
                type: Boolean,
                required: false,
                default: false,
            },
            isTaxable: {
                type: Boolean,
                required: false,
                default: true,
            },
        },
        data() {
            return {
                fields: null,
                fieldErrors: {},
                amounts: config.app.user.deposit.quickAmounts.map((amount) => ({
                    type: amount,
                    displayName: amount
                })),
                isLoading: false,
            };
        },
        created() {
            this.fields = {
                amount: {
                    value: null,
                    validate: [
                        isRequired,
                        min(parseInt(this.config.deposit.onlineMin, 10)),
                        max(parseInt(this.config.deposit.onlineMax, 10)),
                    ]
                }
            };
        },
        computed: {
            ...mapGetters('data/country', [
                'config',
            ]),
        },
        methods: {
            ...mapActions('ui/modal', [
                'setModal',
            ]),
            ...mapActions('errors', [
                'showError',
            ]),
            onAmountClick(amount) {
                this.fields.amount.value = amount;
                this.focusInput();
            },
            async onSubmit() {
                this.isLoading = true;
                await errorGuard({
                    action: async () => {
                        // because we have one additional online deposit on account page!
                        if (this.showWelcomeBonus) {
                            this.setModal(Modals.welcomeBonus.code);
                        } else {
                            const response = await UserService.getInstance().createDeposit(
                                this.fields.amount.value,
                                this.paySafeCard
                            );
                            eventChannel.publish(Topic.Analytics, {
                                depositAmount: this.fields.amount.value,
                            });
                            this.setModal({
                                code: Modals.depositResponse.code,
                                data: response,
                            });
                        }
                    },
                    onError: (e) => {
                        if (e instanceof ValidationError) {
                            this.asyncErrors = e.fieldErrors;
                        } else {
                            this.showError(e);
                        }
                    },
                });
                this.isLoading = false;
            },
            focusInput() {
                this.$nextTick(() => {
                    this.$refs.depositValue.focusInput();
                });
            }
        },
    };
</script>



// WEBPACK FOOTER //
// src/app/modules/userProfile/deposit/shared/DepositAmountContainer.vue